/**
 * Capacitor-only scroll lock utility.
 * Toggles `overflow: hidden` on `.app-root` when an overlay (e.g. bottom sheet)
 * is open inside the native iOS shell. No-op on web.
 */

import { isScrollDebugEnabled } from './scrollContainer';

export type BodyScrollLockToken = number;

const activeLocks = new Set<BodyScrollLockToken>();
let nextToken = 1;
let savedScrollY = 0;
let suppressNextRestore = false;

function debugLog(...args: any[]) {
  if (isScrollDebugEnabled()) {
    console.log('[ScrollLock]', ...args);
  }
}

function isNativePlatform(): boolean {
  return !!(window as any).Capacitor?.isNativePlatform?.();
}

function getAppRoot(): HTMLElement | null {
  return document.querySelector('.app-root') as HTMLElement | null;
}

/**
 * Called during route transitions to avoid stale scroll restores from delayed sheet unmounts.
 */
export function resetBodyScrollLock(): void {
  if (!isNativePlatform()) return;

  const hadLocks = activeLocks.size;
  activeLocks.clear();
  savedScrollY = 0;
  suppressNextRestore = true;

  const appRoot = getAppRoot();
  if (appRoot) {
    appRoot.style.overflow = '';
  }

  debugLog('resetBodyScrollLock', { hadLocks });
}

export function lockBodyScroll(): BodyScrollLockToken | null {
  if (!isNativePlatform()) return null;

  const token = nextToken++;
  activeLocks.add(token);

  if (activeLocks.size === 1) {
    suppressNextRestore = false;
    const appRoot = getAppRoot();
    if (appRoot) {
      savedScrollY = appRoot.scrollTop;
      appRoot.style.overflow = 'hidden';
    }
  }

  debugLog('lockBodyScroll', { token, totalLocks: activeLocks.size, savedScrollY });

  return token;
}

export function unlockBodyScroll(token?: BodyScrollLockToken | null): void {
  if (!isNativePlatform()) return;

  if (typeof token === 'number') {
    const existed = activeLocks.delete(token);
    if (!existed) {
      debugLog('unlockBodyScroll STALE token', { token });
      return;
    }
  } else {
    const firstToken = activeLocks.values().next().value as BodyScrollLockToken | undefined;
    if (firstToken === undefined) return;
    activeLocks.delete(firstToken);
  }

  debugLog('unlockBodyScroll', { token, remainingLocks: activeLocks.size, suppressNextRestore });

  if (activeLocks.size === 0) {
    const appRoot = getAppRoot();
    if (appRoot) {
      appRoot.style.overflow = '';
      if (!suppressNextRestore) {
        debugLog('restoring scrollTop', { savedScrollY });
        appRoot.scrollTop = savedScrollY;
      } else {
        debugLog('suppressed restore');
      }
    }

    savedScrollY = 0;
    suppressNextRestore = false;
  }
}
